import { GFG_LOGO } from "@/constants/campaignBuilder";
import {
  IMPACT_REPORT_ALL_TIME_LABEL,
  IMPACT_REPORT_MASTHEAD_LABEL,
  IMPACT_REPORT_MASTHEAD_LOGO_ALT,
  IMPACT_REPORT_PREPARED_BY_LABEL,
} from "@/constants/impactReport";
import type { ReportData } from "@/interfaces/impactReport";
import { formatDateLong } from "@/utils/date-format";

interface ImpactReportMastheadProps {
  /**
   * The MATCHED bounds of what the report covers — never an echo of the requested window. Both
   * ends are `null` together on an empty result set.
   */
  dateRange: ReportData["dateRange"];
  /**
   * The lockup image. Defaults to `GFG_LOGO`, the R2-hosted mark every on-screen render uses; the
   * PDF serialiser passes the same mark as a `data:` URI, because a converter that cannot reach R2
   * would otherwise print this masthead with a missing image and no error anywhere.
   */
  lockupSrc?: string;
}

/**
 * The masthead above the document: an org identity slot (left) and the GfG report chrome
 * (right) — `IMPACT GIFT REPORT`, the matched date range, and the prepared-by line.
 *
 * Ported from the prototype's `irRenderMasthead()` (client-experience-prototype.html, ~8602–8635).
 *
 * THE LOCKUP IS THE GfG MARK, NOT THE CLIENT'S NAME. An earlier version rendered `orgName` here as
 * a text wordmark, following the prototype's scenario branch. Seen against real data that reads as
 * an unfinished placeholder AND is redundant: the client is already named, far more prominently,
 * by the cover's own kicker ("LUMEN ROBOTICS IS"). The prototype's own demo branch shows the mark
 * for exactly this reason.
 *
 * The end state is the CLIENT's uploaded logo with this mark as the fallback — the shape the
 * prototype uses (`const src = client || IRV_GFG_LOGO`). Neither Crunchy's payload nor Platform's
 * `organizations` table carries a logo field today, so there is nothing to prefer over it yet;
 * when one lands, it slots in ahead of this `src` and nothing else here changes.
 */
export function ImpactReportMasthead({
  dateRange,
  lockupSrc = GFG_LOGO,
}: ImpactReportMastheadProps) {
  // Read straight off the payload, no recomputation — and DELIBERATELY frozen against a cause
  // filter, same as the cover's vendor/type counts in `ImpactHeroPage.tsx`. These bounds are
  // counted from the account's headline totals, not from the per-cause census a cause filter
  // narrows, so selecting one cause changes which cause pages print without moving this line.
  // Both ends are `null` together on a genuinely empty result — that is a real state with its own
  // label below, never a signal to fall back to whatever date window was requested.
  const dateRangeLabel =
    dateRange.from && dateRange.to
      ? `${formatDateLong(dateRange.from)} – ${formatDateLong(dateRange.to)}`
      : IMPACT_REPORT_ALL_TIME_LABEL;

  return (
    <div className="irv-masthead irv-masthead--report">
      <div className="irv-lockup">
        <img src={lockupSrc} alt={IMPACT_REPORT_MASTHEAD_LOGO_ALT} />
      </div>
      <div className="irv-masthead-right">
        <div className="irv-report-title">{IMPACT_REPORT_MASTHEAD_LABEL}</div>
        <div className="irv-report-dates">{dateRangeLabel}</div>
        <div className="irv-report-prepared">{IMPACT_REPORT_PREPARED_BY_LABEL}</div>
      </div>
    </div>
  );
}
